import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  Skeleton,
} from "@mui/material";
import { useAppContext } from "../../context/AppContext";
import { UserSession } from "../../types";

const NAME_CHARACTER_LIMIT = 18;

function truncateWithEllipsis(value: string, limit: number): string {
  if (value.length <= limit) return value;
  return `${value.slice(0, limit)}...`;
}

function formatDate(isoString: string | Date | undefined): string {
  if (!isoString) {
    return "—";
  }

  try {
    const date = new Date(isoString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "—";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "—";
  }
}

interface DistributorTableProps {
  profileData: UserSession[];
}

export default function DistributorTable({
  profileData,
}: DistributorTableProps) {
  const {
    setProfileId,
    setCurrentView,
    setProfileImageUrl,
    setOutputScore,
    getTranslation,
    getSkinTypeTranslation,
    getGenderTranslation,
    distributorSessionsCache,
  } = useAppContext();
  
  // Media query for mobile view
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // Use cached loading state
  const isLoading = distributorSessionsCache.isLoading;

  // Filter all distributor data (distributors have role = 1 and is_anonymous = true)
  const filteredDistributorData = profileData
    .filter((row) => {
      return row.role === 1 && row.is_anonymous === true;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "auto" },
        padding: 0,
        height: { xs: "100%", md: "auto" },
        flex: { xs: 1, md: "none" },
        display: { xs: "flex", md: "block" },
        flexDirection: { xs: "column", md: "row" },
        minHeight: 0,
        overflow: { xs: "hidden", md: "visible" },
      }}
    >
    <Typography sx={{ fontSize: { xs: "14px", sm: "16px", md: "18px" }, fontWeight: 700, color: "var(--color-text-primary)", fontFamily: "HerbalifeFont", textAlign: "left" }}>{getTranslation("Txt937")}</Typography>
      {isXs ? (
        // Mobile design - Card layout with labels
        <Box sx={{ 
          width: "100%", 
          display: "flex", 
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
        }}>
          {/* Mobile Cards List */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            {isLoading ? (
              // Skeleton loaders for mobile view
              Array.from({ length: 3 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    p: "16px 5px",
                    backgroundColor: index % 2 === 0 ? "var(--color-background)" : "#ffffff",
                    borderBottom: "2px solid #000",
                    borderRadius: "10px",
                    mb: 2,
                  }}
                >
                  {/* Row 1: Date, Name, Gender */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: "8px" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                      <Skeleton variant="text" width="40%" height={16} />
                      <Skeleton variant="text" width="60%" height={14} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                      <Skeleton variant="text" width="50%" height={16} />
                      <Skeleton variant="text" width="70%" height={14} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                      <Skeleton variant="text" width="45%" height={16} />
                      <Skeleton variant="text" width="55%" height={14} />
                    </Box>
                  </Box>
                  {/* Row 2: Age Group, Skin Type, Detailed Report */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                      <Skeleton variant="text" width="50%" height={16} />
                      <Skeleton variant="text" width="60%" height={14} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                      <Skeleton variant="text" width="45%" height={16} />
                      <Skeleton variant="text" width="65%" height={14} />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", flex: 1, justifyContent: "flex-start" }}>
                      <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: "4px" }} />
                    </Box>
                  </Box>
                </Box>
              ))
            ) : filteredDistributorData.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 2, mt: 10, color: "var(--color-text-secondary)", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {getTranslation("Txt999")}
              </Box>
            ) : (
              filteredDistributorData.map((row, index) => {
                const fullName = `${row.first_name || ""} ${row.last_name || ""}`.trim();
                const displayName = fullName || row.guest_name || "Distributor";
                const truncatedDisplayName = truncateWithEllipsis(displayName, NAME_CHARACTER_LIMIT);

                return (
                  <Box
                    key={index}
                    sx={{
                      p: "16px 5px",
                      backgroundColor: index % 2 === 0 ? "var(--color-background)" : "#ffffff",
                      borderBottom: "2px solid #000",
                      borderRadius: "10px",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    {/* Row 1: Date, Name, Gender */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: "8px" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", fontSize: "14px", color: "var(--color-text-primary)", textAlign: "left", flex: 1 }}>
                        <span style={{ fontWeight: 600, fontSize: "14px" }}>{getTranslation("Txt277")} </span>
                        <span style={{ fontWeight: 400, fontSize: "13px" }}>{formatDate(row.createdAt)}</span>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", fontSize: "14px", color: "var(--color-text-primary)", textAlign: "left", flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{getTranslation("Txt279")} </span>
                        <span style={{ fontWeight: 400 }}>{truncatedDisplayName}</span>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", fontSize: "14px", color: "var(--color-text-primary)", textAlign: "left", flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{getTranslation("Txt883")}: </span>
                        <span style={{ fontWeight: 400 }}>{row.gender ? getGenderTranslation(row.gender) : "—"}</span>
                      </Box>
                    </Box>

                    {/* Row 2: Age Group, Skin Type, Detailed Report */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", fontSize: "14px", color: "var(--color-text-primary)", textAlign: "left", flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{getTranslation("Txt884")}: </span>
                        <span style={{ fontWeight: 400, fontSize: "13px" }}>{row.age_range || "—"}</span>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", fontSize: "14px", color: "var(--color-text-primary)", textAlign: "left", flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{getTranslation("Txt885")}: </span>
                        <span style={{ fontWeight: 400 }}>{row.skin_type ? getSkinTypeTranslation(row.skin_type) : "—"}</span>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                        <Box
                          onClick={(event) => {
                            event.preventDefault();
                            setProfileId(row._id || "");
                            setProfileImageUrl(row.input_image_url || null);
                            setOutputScore(row.output_score && row.output_score.length > 0 ? row.output_score : null);
                            setCurrentView("MoreUserDetails");
                          }}
                          sx={{
                            backgroundColor: "var(--color-primary)",
                            color: "var(--color-text-inverse)",
                            padding: "8px 12px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: 500,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            textAlign: "center",
                            width: "fit-content",
                            "&:hover": { 
                              backgroundColor: "#333",
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                            },
                            "&:active": {
                              transform: "translateY(0px)",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.2)"
                            },
                            transition: "all 0.2s ease-in-out"
                          }}
                        >
                          <span style={{ fontSize: "12px", fontWeight: 500, marginBottom: "-2px" }}>{getTranslation("Txt283")}</span>
                          <span style={{ fontSize: "12px", fontWeight: 500 }}>&#10093;</span>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "10px",
            boxShadow: 0,
            backgroundColor: "var(--color-background)",
            minHeight: { xs: "100%", md: "300px" },
            maxHeight: { xs: "100%", md: "300px" },
            height: { xs: "100%", md: "auto" },
            overflow: "visible",
            position: "relative",
            display: { xs: "flex", md: "block" },
            flexDirection: "column",
            flex: 1,
            overflowY: "auto",
          }}
        >
        <Table
          sx={{
            minWidth: { xs: 800, md: "auto" },
            backgroundColor: "var(--color-background)",
            height: { xs: "100%", md: "auto" },
          }}
        >
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 9, 
              }}
            >
              <TableRow sx={{ backgroundColor: "#D2E1B4" }}>
                {[
                  getTranslation("Txt615"),
                  getTranslation("Txt939"),
                  getTranslation("Txt883"),
                  getTranslation("Txt884"),
                  getTranslation("Txt885"),
                  getTranslation("Txt492"),
                ].map((header) => (
                  <TableCell
                    key={String(header)}
                    sx={{
                      ...headerStyle,
                      display: "table-cell",
                      width: "16.66%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                // Skeleton loaders for desktop table view
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:nth-of-type(even)": { backgroundColor: "var(--color-background)" },
                      borderBottom: "none",
                    }}
                  >
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="text" width="80%" height={20} />
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="text" width="70%" height={20} />
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="text" width="50%" height={20} />
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="text" width="40%" height={20} />
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="text" width="60%" height={20} />
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: "4px" }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                filteredDistributorData.length === 0 ? (
                  <TableRow
                    sx={{
                      backgroundColor: "var(--color-background)",
                      borderBottom: "none",
                    }}
                  >
                    <TableCell colSpan={6} sx={{ ...cellStyle, textAlign: "center", py: 4, width: "100%" }}>
                      <Typography variant="body2" sx={{ color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
                        {getTranslation("Txt999")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                filteredDistributorData.map((row, index) => {
                  const fullName = `${row.first_name || ""} ${row.last_name || ""}`.trim();
                  const displayName = fullName || row.guest_name || "Distributor";
                  const truncatedDisplayName = truncateWithEllipsis(displayName, NAME_CHARACTER_LIMIT);

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(even)": { backgroundColor: "var(--color-background)" },
                        borderBottom: "none",
                      }}
                    >
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "16.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={formatDate(row.createdAt)}
                      >
                        {formatDate(row.createdAt)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "20.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                        title={displayName}
                      >
                        {truncatedDisplayName}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "16.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={row.gender ? getGenderTranslation(row.gender) : "—"}
                      >
                        {row.gender ? getGenderTranslation(row.gender) : "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "16.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={row.age_range || "—"}
                      >
                        {row.age_range || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "16.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={row.skin_type ? getSkinTypeTranslation(row.skin_type) : "—"}
                      >
                        {row.skin_type
                          ? getSkinTypeTranslation(row.skin_type)
                          : "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "16.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Link
                          onClick={(event) => {
                            event.preventDefault();

                            setProfileId(row._id || "");
                            // Set the profile image URL from the session data
                            setProfileImageUrl(row.input_image_url || null);
                            // Pass output_score to context for MoreUserDetails if available
                            setOutputScore(row.output_score && row.output_score.length > 0 ? row.output_score : null);
                            setCurrentView("MoreUserDetails");
                          }}
                          href="#"
                          sx={{
                            backgroundColor: "var(--color-primary)",
                            color: "var(--color-text-inverse)",
                            padding: "8px 12px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: 500,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            textAlign: "center",
                            width: "fit-content",
                            "&:hover": { 
                              backgroundColor: "#333",
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                            },
                            "&:active": {
                              transform: "translateY(0px)",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.2)"
                            },
                            transition: "all 0.2s ease-in-out"
                          }}
                        >
                          <span style={{ fontWeight: 500, marginBottom: "-2px" }}>{getTranslation("Txt283")}</span>
                          <span style={{ fontWeight: 500 }}>&#10093;</span>
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

const headerStyle = {
  fontWeight: 700,
  fontSize: { xs: "10px", md: "14px" },
  textAlign: "left",
  color: "#000000",
  backgroundColor: "#D2E1B4",
  lineHeight: { xs: "16px", md: "20px" },
  padding: { xs: "12px 8px 6px 8px", md: "20px 20px 8px 20px" },
  whiteSpace: "nowrap",
  width: { xs: "60px", md: "auto" },
};

const cellStyle = {
  fontSize: { xs: "11px", md: "16px" },
  color: "var(--color-text-primary)",
  fontWeight: 400,
  textAlign: "left",
  margin: "auto",
  lineHeight: { xs: "16px", md: "20px" },
  padding: { xs: "8px 6px 8px 6px", md: "10px 20px 10px 20px" },
  borderBottom: "none",
  whiteSpace: "nowrap",
  width: { xs: "60px", md: "auto" },
};
